import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  equals,
  findIndex,
  includes,
  join,
  lensIndex,
  map,
  prop,
  propEq,
  reject,
  set,
  sort,
  uniqBy,
} from "ramda";
import { validateContent, validateContentField } from "../contentValidation";
import { allTrue, notBlank } from "../../../util/ext";
import {
  optionalContentFieldsMapper,
  getDropdownList,
  byLabel,
} from "./optionalContentFieldsUtils";

import Grid from "@material-ui/core/Grid";
import TextDirectionSelect from "./TextDirectionSelect";
import TextField from "@material-ui/core/TextField";
import EditColumn from "../../common/EditColumn";
import OptionalFieldDropDown from "./OptionalFieldDropDown";
import { MediaContext } from "../../common/media-context";
import AgeRangeSelect from "./AgeRangeSelect";
import ColumnHeader from "./ColumnHeader";
import { IconButton, InputAdornment } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import VideoRatingsSelect from "../../../manage-csp/edit/VideoRatingsSelect";
import { useIsAdmin } from "../../../auth/authUtils";

const validator = {
  validateField: validateContentField,
  validate: validateContent,
};

const OptionalContentField = props => {
  const isAdmin = useIsAdmin();
  const { optionalField, ...otherProps } = props;

  const isInternalPublisherId = optionalField.label === "Internal Publisher ID";

  const label = allTrue([isInternalPublisherId, !isAdmin])
    ? "Internal ID"
    : optionalField.label;
  return (
    <Grid item xs={12}>
      <TextField
        {...otherProps}
        // prevent non-Admin users from editing internalPublisherId
        disabled={allTrue([isInternalPublisherId, !isAdmin])}
        id={`content-${join("-", optionalField.fieldName)}`}
        fullWidth
        label={`${label} (optional)`}
      />
    </Grid>
  );
};

const OptionalSelectField = ({ optionalField, ...selectProps }) => {
  const Select = equals(optionalField.label, "Text Directionality")
    ? TextDirectionSelect
    : equals(optionalField.label, "Age Range")
    ? AgeRangeSelect
    : VideoRatingsSelect;
  return (
    <Grid item key={`key-${join("-", optionalField.fieldName)}`}>
      <Select {...selectProps} />
    </Grid>
  );
};

const EditContentOptionalColumn = props => {
  const { className } = props;
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();

  const [optionalFieldDropdownList, setOptionalFieldDropdownList] = useState(
    [],
  );

  // Manage dropdown list
  useEffect(() => {
    if (notBlank(context.initial)) {
      const formattedOptionalFields = optionalContentFieldsMapper(
        context.initial.type,
      );

      const dropdownList = getDropdownList(context.initial)(
        formattedOptionalFields,
      );

      setOptionalFieldDropdownList(dropdownList);
    }
  }, [context.initial]);

  // Event Handlers
  const onOptionalFieldTextChange = optionalField => event => {
    const value = event.target.value;

    const updatedOptionalFields = map(prevOptionalField => {
      if (equals(prevOptionalField.label, optionalField.label)) {
        return { ...prevOptionalField, value: value };
      }
      return prevOptionalField;
    }, context.optionalFields);

    context.setOptionalFields(updatedOptionalFields);
  };

  const handleDropdownItemClick = useCallback(
    optionalField => {
      setOptionalFieldDropdownList(prevList =>
        reject(propEq("label", optionalField.label), prevList),
      );

      optionalField.value = "";
      context.setOptionalFields(prevList =>
        uniqBy(prop("label"), [...prevList, optionalField]),
      );
    },
    [context],
  );

  const handleRemoveOptionalField = useCallback(
    optionalField => {
      setOptionalFieldDropdownList(prevList =>
        sort(byLabel, uniqBy(prop("label"), [...prevList, optionalField])),
      );

      optionalField.value = null;
      context.setOptionalFields(prevList => {
        const index = findIndex(propEq("label", optionalField.label), prevList);
        const optionalFields = set(lensIndex(index), optionalField, prevList);
        return uniqBy(prop("label"), optionalFields);
      });
    },
    [context],
  );

  const formBuilder = handlers => {
    return (
      <Fragment>
        {/* Optional Fields */}
        <ColumnHeader
          header="Optional Fields"
          paddingBottom={0}
          customButton={
            <OptionalFieldDropDown
              optionalFields={optionalFieldDropdownList}
              contentType={context.media.type}
              handleItemClick={handleDropdownItemClick}
            />
          }
        />
        {map(optionalField => {
          return includes(optionalField.label, [
            "Age Range",
            "Rating",
            "Text Directionality",
          ]) ? (
            <OptionalSelectField
              optionalField={optionalField}
              value={optionalField.value}
              onChange={onOptionalFieldTextChange(optionalField)}
            />
          ) : (
            <OptionalContentField
              key={`key-${join("-", optionalField.fieldName)}`}
              optionalField={optionalField}
              onChange={onOptionalFieldTextChange(optionalField)}
              value={optionalField.value ? optionalField.value : ""}
              inputProps={optionalField.inputProps}
              helperText={
                optionalField.label === "Keywords"
                  ? "Keywords should be separated by commas"
                  : optionalField.label === "Lexile Level"
                  ? "Numerical values only"
                  : null
              }
              InputProps={{
                // prevent non-Admin users from removing internalPublisherId
                endAdornment: allTrue([
                  !isAdmin,
                  optionalField.label === "Internal Publisher ID",
                ]) ? null : (
                  <InputAdornment>
                    <IconButton
                      onClick={() => handleRemoveOptionalField(optionalField)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }, reject(propEq("value", null), context.optionalFields))}
      </Fragment>
    );
  };

  return (
    <EditColumn
      className={className}
      validator={validator}
      formBuilder={formBuilder}
    />
  );
};

export default EditContentOptionalColumn;
